import { useEffect, useState } from 'react'
import { onIdle } from '../api/idle'
import { CodeBoxes } from '../login/CodeBoxes'

/**
 * "Ai fost deconectat pentru inactivitate", si drumul inapoi.
 *
 * `_layout/_idle_bar.php` + `public/js/idle.js` din portal (livrarea
 * 26.9.10.1), cu acelasi rol (`public/css/idle.css`): sta ascunsa in fiecare
 * pagina si se aprinde cand o cerere se intoarce cu 401 si motivul `idle`
 * (`api/idle.ts`). Scrisa in schelet, nu intr-un ecran anume: expirarea nu are
 * ecran preferat, se intampla acolo unde a fost lasata pagina.
 *
 * Doua trepte: Send trimite codul pe adresa contului
 * (`POST /api/v2/auth/idle/send`). Cat se trimite, Send sta pe ecran (scrie
 * Sending...); dupa ce a plecat, Send dispare si ramane doar Enter Code - cele
 * sase casute, una pe cifra, ca la login. Cand se umple ultima, Enter Code
 * pleaca singur; butonul ramane pentru cine vrea sa apese. Cod bun - pagina se
 * reincarca: cat a stat inactiva, tot ce era pe ecran s-a invechit. Adresa e
 * aceeasi, deci omul ramane unde era.
 *
 * Bara e rosie: e o deconectare, nu un mesaj de informare. Nu are X: cat timp
 * e acolo, pagina de deasupra nu mai raspunde oricum, iar un buton de
 * inchidere ar fi spus ca s-a rezolvat ceva.
 *
 * `fetch` direct, nu `apiPost`: raspunsurile de aici sunt 401 pe drumul bun
 * (jetonul E expirat), si `client.ts` le-ar trimite la intrare.
 */
export const IDLE_SEND_URL = '/api/v2/auth/idle/send'
export const IDLE_RESUME_URL = '/api/v2/auth/idle/resume'

type Reply = { ok?: boolean; sent?: boolean; wait?: number; message?: string; error?: string; gone?: boolean }

async function post(url: string, fields: Record<string, string> = {}): Promise<{ status: number; body: Reply }> {
  const body = new URLSearchParams(fields)
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { Accept: 'application/json', 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  })
  let parsed: Reply = {}
  try {
    parsed = (await response.json()) as Reply
  } catch {
    /* fara corp */
  }
  return { status: response.status, body: parsed }
}

export function IdleBar({ reload = () => window.location.reload() }: { reload?: () => void }) {
  const [minutes, setMinutes] = useState<number | null>(null)
  const [say, setSay] = useState('')
  const [bad, setBad] = useState(false)
  const [step2, setStep2] = useState(false)
  const [sending, setSending] = useState(false)
  const [checking, setChecking] = useState(false)
  const [code, setCode] = useState('')
  /* Dupa un cod gresit, casutele se remonteaza goale. */
  const [boxesKey, setBoxesKey] = useState(0)

  useEffect(() => onIdle((m) => setMinutes((was) => was ?? (m > 0 ? m : 60))), [])

  useEffect(() => {
    if (minutes === null) return
    document.body.classList.add('od-idle-on')
    return () => document.body.classList.remove('od-idle-on')
  }, [minutes])

  if (minutes === null) return null

  const tell = (text: string, isBad = false): void => {
    setSay(text)
    setBad(isBad)
  }

  const send = async (): Promise<void> => {
    setSending(true)
    tell('Sending...')
    try {
      const { status, body } = await post(IDLE_SEND_URL)
      if (status === 401 || body.gone) {
        tell('Your session is gone. Please sign in again.', true)
        return
      }
      tell(body.message ?? body.error ?? 'A code is on its way.', !body.ok)
      if (body.ok) {
        setStep2(true)
        setCode('')
        setBoxesKey((k) => k + 1)
      }
    } catch {
      tell('We could not send the code.', true)
    } finally {
      /* Ca in idle.js: butonul revine dupa trei secunde, nu la clic. */
      window.setTimeout(() => setSending(false), 3000)
    }
  }

  const go = async (typedCode?: string): Promise<void> => {
    const typed = (typedCode ?? code).trim()
    if (typed.length !== 6) {
      return
    }
    setChecking(true)
    tell('Checking...')
    try {
      const { status, body } = await post(IDLE_RESUME_URL, { code: typed })
      if (body.ok) {
        tell('Welcome back.')
        reload()
        return
      }
      tell(status === 401 ? 'Your session is gone. Please sign in again.' : (body.error ?? 'That did not work.'), true)
      setCode('')
      setBoxesKey((k) => k + 1)
    } catch {
      tell('That did not work.', true)
      setCode('')
      setBoxesKey((k) => k + 1)
    } finally {
      setChecking(false)
    }
  }

  return (
    <div className="od-idle-bar" id="odIdleBar" role="alert" aria-live="assertive">
      <div className="od-idle-what">
        <i className="fas fa-user-clock" aria-hidden="true"></i>
        <div>
          <strong>You were signed out for inactivity.</strong>
          <span>
            Nothing was lost - the page is still here. Send yourself a code to carry on (
            <span id="odIdleFor">{minutes}</span> minutes without a keystroke).
          </span>
        </div>
      </div>
      <div className="od-idle-act">
        <span className={'od-idle-say' + (bad ? ' od-idle-bad' : '')} id="odIdleSay">
          {say}
        </span>
        {/* Send doar pana a plecat codul: dupa aceea ar arata ca mai trebuie
            trimis o data, desi pe ecran stau deja casutele de introdus. */}
        {!step2 ? (
          <button type="button" className="btn btn-default btn-sm" id="odIdleSend" disabled={sending} onClick={() => void send()}>
            <i className="fas fa-paper-plane" aria-hidden="true"></i> {sending ? 'Sending...' : 'Send'}
          </button>
        ) : (
          <span id="odIdleStep2" className="od-idle-step2">
            <CodeBoxes
              key={boxesKey}
              length={6}
              disabled={checking}
              onComplete={(filled) => {
                setCode(filled)
                void go(filled)
              }}
            />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              id="odIdleGo"
              disabled={checking || code.length !== 6}
              onClick={() => void go()}
            >
              Enter Code
            </button>
          </span>
        )}
      </div>
    </div>
  )
}
